import React, { useEffect, useMemo } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd/es/menu';
import { useSimpleModal } from '../../Modal/Simple';
import { CommentData, CommentStatus, useCommentDelete, useCommentUpdate } from '../../../../hooks/api/comments';
import PostOrCommentTemplate from '../../PostOrCommentTemplate';

export interface CommentProps {
  data: CommentData;
  setPage: (page: number) => void;
}

export default function SubComment({ data, setPage }: CommentProps): JSX.Element {
  const navigate = useNavigate();
  const { modal, contextHolder } = useSimpleModal();
  const commentDelete = useCommentDelete(data.id);
  const commentUpdate = useCommentUpdate(data.id);

  useEffect(() => {
    if (commentDelete.data && !commentDelete.error && !commentDelete.loading) {
      setPage(1);
    }
  }, [commentDelete.data]);

  useEffect(() => {
    if (commentUpdate.data && !commentUpdate.error && !commentUpdate.loading) {
      setPage(1);
    }
  }, [commentUpdate.data]);

  const confirmDelete = () => {
    if (data.id) {
      modal.confirm({
        title: 'Are you sure you want to Delete the comment?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => commentDelete.fetch(),
        okText: 'Delete',
      });
    }
  };

  const confirmDeactivate = () => {
    modal.confirm({
      title: 'Are you sure you want to Deactivate the comment?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => commentUpdate.fetch({
        status: CommentStatus.DEACTIVATED,
      }),
      okText: 'Deactivate',
    });
  };

  const confirmPublish = () => {
    modal.confirm({
      title: 'Are you sure you want to Publish the comment?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => commentUpdate.fetch({
        status: CommentStatus.PUBLISHED,
      }),
      okText: 'Publish',
    });
  };

  const dropdownMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          key: '1',
          label: 'Edit',
          onClick: () => navigate(`/comments/${data.id}/update`),
        },
        {
          key: '2',
          label: 'Delete',
          onClick: confirmDelete,
        },
        data.status === 'published'
          ? {
            key: '3',
            label: 'Hide',
            onClick: confirmDeactivate,
          }
          : {
            key: '3',
            label: 'Publish',
            onClick: confirmPublish,
          },
      ],
    }),
    [data],
  );

  return (
    <>
      <PostOrCommentTemplate data={data} type="subComments" dropdownMenu={dropdownMenu} />
      {contextHolder}
    </>
  );
}
